import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage } from '@scisys-uim/uim-common';
import { ASSETS_USER_DETAILS_TYPE, AssetsUser, AssetsUserDetails, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, setDefaultLocationFromUser } from 'assets-model';
import { AssetsUserDetailsService, BusinessUnitService } from 'assets-service';


@Directive()
export abstract class AssetsUserDetailsItemComponentBase extends CrudItemComponent<AssetsUserDetails, string> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  businessUnitSelectionList: BusinessUnit[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected assetsUserDetailsService: AssetsUserDetailsService,
    protected businessUnitService: BusinessUnitService,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(ASSETS_USER_DETAILS_TYPE, fb, elementRef, router, route, userStorage, dialog, assetsUserDetailsService, injector, snackbar);
  }

  createForm(): FormGroup {
    return this.fb.group({
      user: [null, []],
      businessUnit: [null, []],
      landline: [null, []],
      lockVersion: [null, []],
      mobile: [null, []],
      notificationSummaryEmails: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.user?.disable();
    }
  }
  
  userChanging(value: string, unsetDependents: boolean = true) {}
  
  userChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.userChangedByUser(value, unsetDependents);
    }
  }  
  
  userChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  landlineChanging(value: string, unsetDependents: boolean = true) {}
  
  landlineChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.landlineChangedByUser(value, unsetDependents);
    }
  }  
  
  landlineChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  mobileChanging(value: string, unsetDependents: boolean = true) {}
  
  mobileChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.mobileChangedByUser(value, unsetDependents);
    }
  }  
  
  mobileChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  notificationSummaryEmailsChanging(value: boolean, unsetDependents: boolean = true) {}
  
  notificationSummaryEmailsChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.notificationSummaryEmailsChangedByUser(value, unsetDependents);
    }
  }  
  
  notificationSummaryEmailsChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get user() {
    return this.control('user');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get landline() {
    return this.control('landline');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get mobile() {
    return this.control('mobile');
  }
  
  get notificationSummaryEmails() {
    return this.control('notificationSummaryEmails');
  }
  
}





